<template>
    <div>
        <b-row v-if="!printView || payments.length">
            <b-col class="header-menu-text">
                <h5 class="text-secondary" v-if="payments.length === 1">Payment</h5>
                <h5 class="text-secondary" v-else>Payments</h5>
            </b-col>

            <b-col align="right" v-if="!printView">
                <b-dropdown ref="paymentDropdown" no-caret size="sm" variant="link" class="menu-btn" toggle-class="text-decoration-none">
                    <template #button-content>
                        <b-button variant="outline-secondary" size="sm">
                            <b-icon-plus></b-icon-plus>
                        </b-button>
                    </template>

                    <payment @addPayment="addPayment(null, $event)"></payment>
                </b-dropdown>
            </b-col>
        </b-row>

        <div v-if="!printView">
            <b-row v-if="this.component.length < 1">
                <b-col align="center">
                    <b-list-group>
                        <b-list-group-item variant="light">
                            <small><em>No Payments</em></small>
                        </b-list-group-item>
                    </b-list-group>
                </b-col>
            </b-row>

            <b-row v-for="(payment, index) in component" v-bind:key="'payment-' + index" class="mb-3">
                <b-col>
                    <b-input-group size="sm" :prepend="payment.method">
                        <b-input class="py-3" :value="'$' + payment.amount" disabled></b-input>
                        <b-link style="text-overflow: ellipsis; overflow: hidden;" v-if="payment.transactionId || (platform && platform.site === 'Discogs')" class="form-control text-primary py-3 paymentLink" target="payment" :href="paymentLink(payment.method, payment.transactionId)">{{ getPaymentLinkText(payment) }}</b-link>

                        <b-input-group-append v-if="payment.username">
                            <b-button size="sm" :id="'paymentuser-' + index" variant="outline-info">
                                <b-icon-person></b-icon-person>
                            </b-button>

                            <b-tooltip :target="'paymentuser-' + index" triggers="hover">
                                {{ payment.username }}
                            </b-tooltip>
                        </b-input-group-append>

                        <b-input-group-append v-if="payment.date">
                            <b-button size="sm" :id="'paymentdate-' + index" variant="outline-info">
                                <b-icon-calendar4-event></b-icon-calendar4-event>
                            </b-button>

                            <b-tooltip :target="'paymentdate-' + index" triggers="hover">
                                {{ moment(payment.date).format('M/D/YYYY h:mma') }}
                            </b-tooltip>
                        </b-input-group-append>
                        <template #append>
                            <b-dropdown ref="existingPaymentDropdown" no-caret size="sm" variant="outline-info" toggle-class="text-decoration-none">
                                <template #button-content>
                                    <b-button class='editButton p-0' variant='outline-info' size="sm">
                                        <b-icon-pencil></b-icon-pencil>
                                    </b-button>
                                </template>
                                <payment :initialAmount='payment.amount' :initialMethod="payment.method" :initialTransactionId="payment.transactionId" :initialNote="payment.note" @addPayment="addPayment(index, $event)" @removePayment="removePayment(index, $event)"></payment>
                            </b-dropdown>
                            <b-button
                                v-if="canRefund(payment)"
                                :id="`refund-btn-${index}`"
                                size="sm"
                                variant="outline-warning"
                                class="refundButton"
                                @click="openRefundModal(payment)"
                            >
                                <b-icon-arrow-return-left></b-icon-arrow-return-left>
                            </b-button>
                            <b-tooltip
                                v-if="canRefund(payment)"
                                :target="`refund-btn-${index}`"
                                triggers="hover"
                            >
                                Refund this payment
                            </b-tooltip>
                        </template>
                        
                    
                    </b-input-group>

                    <b-input-group size="sm" v-if="payment.note">
                        <em class="form-control disabled">{{ payment.note }}</em>
                    </b-input-group>
                </b-col>
            </b-row>
        </div>

        <div v-else>
            <div v-for="(payment, index) in payments" v-bind:key="'payment-' + index">
                <b-row>
                    <b-col sm="3">Method</b-col>
                    <b-col>{{ payment.method }}</b-col>
                </b-row>
                <b-row>
                    <b-col sm="3">Transaction</b-col>
                    <b-col><b-link target="payment" :href="paymentLink(payment.method, payment.transactionId)">{{ payment.transactionId }}</b-link></b-col>
                </b-row>
                <b-row>
                    <b-col sm="3">Date</b-col>
                    <b-col>{{ moment(payment.date).format('M/D/YYYY h:mma') }}</b-col>
                </b-row>
                <b-row v-if="!printView">
                    <b-col sm="3">Amount</b-col>
                    <b-col>${{ payment.amount }}</b-col>
                </b-row>
            </div>
        </div>
        <b-modal
            id="paymentRefundModal"
            v-model="showRefundModal"
            centered
            :title="refundPayment ? 'Refund ' + refundPayment.method + ' Payment' : 'Refund Payment'"
        >
            <div v-if="refundPayment">
                <p class="mb-2">
                    <strong>Original amount:</strong>
                    ${{ Number(refundPayment.amount).toFixed(2) }}
                </p>
                <p class="mb-2" v-if="totalRefundedForPayment > 0">
                    <strong>Already refunded:</strong>
                    ${{ totalRefundedForPayment.toFixed(2) }}
                </p>
                <p class="mb-3">
                    <strong>Maximum refundable:</strong>
                    ${{ maxRefundAmount.toFixed(2) }}
                </p>

                <b-form-group label="Amount to refund" label-for="refund-amount-input" label-size="sm">
                    <b-input-group prepend="$" size="sm">
                        <b-form-input
                            id="refund-amount-input"
                            type="number"
                            v-model.number="refundAmount"
                            :max="maxRefundAmount"
                            min="0.01"
                            step="0.01"
                        ></b-form-input>
                    </b-input-group>
                </b-form-group>

                <b-form-group label="Note (optional)" label-for="refund-note-input" label-size="sm">
                    <b-form-input
                        id="refund-note-input"
                        v-model="refundNote"
                        size="sm"
                        placeholder="Reason for refund"
                    ></b-form-input>
                </b-form-group>

                <b-alert v-if="refundError" variant="danger" show class="py-1">
                    {{ refundError }}
                </b-alert>
            </div>

            <template #modal-footer>
                <b-button size="sm" variant="secondary" class="mr-2" @click="closeRefundModal">
                    Cancel
                </b-button>
                <b-button
                    size="sm"
                    variant="primary"
                    :disabled="isSubmittingRefund || !canSubmitRefund"
                    @click="confirmRefund"
                >
                    {{ isSubmittingRefund ? 'Processing...' : 'Submit Refund' }}
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<script>
    import moment from "moment";
    import Payment from "./Payment";
    import arrayMixin from "../../mixins/array";
    import apiClient from "@/services/apiClient";

    export default {
        name: "PaymentList",
        props: [ 'payments', 'printView', 'email', 'platform', 'refunds', 'orderId', 'order' ],
        mixins: [ arrayMixin ],
        components: { Payment },
        created() {
            this.clone(this.payments);
            console.log(`orderId`, this.orderId);
        },
        data() {
            return {
                showRefundModal: false,
                refundPayment: null,
                refundAmount: null,
                refundNote: '',
                maxRefundAmount: 0,
                totalRefundedForPayment: 0,
                isSubmittingRefund: false,
                refundError: null,
            };
        },
        methods: {
            
            addPayment(index, payment) {
                if (!index && index !== 0) {
                    this.component.push(payment);
                } 
                else {
                    const updatedArray = this.component.map((item, i) => i === index ? payment : item);
                    this.component = updatedArray;
                }

                // this.$emit('update', payment);

                this.closeDropdown(index);
            },
            removePayment(index) {
                this.component.splice(index, 1)
                this.closeDropdown(index);
            },

            paymentLink(method, transactionId) {
                if (method === 'PayPal') {
                    if (this.platform && this.platform.site === 'Discogs') {
                        let today = moment().format('YYYY-MM-DD');
                        let monthAgo = moment().subtract(3,  'months').format('YYYY-MM-DD');
                        return `${process.env.VUE_APP_PAYPAL_EMAIL_URL}${this.email}&fromDate=${monthAgo}&toDate=${today}`;
                    } else {
                        return `${process.env.VUE_APP_PAYPAL_TXN_URL}${transactionId}`;
                    }
                } 
                
                return `${process.env.VUE_APP_STRIPE_TXN_URL}${transactionId}`;
            },
            getPaymentLinkText(payment) {
                if (this.platform && this.platform.site === 'Discogs') {
                    return 'Search by Email';
                }

                return payment.transactionId;
            },
            closeDropdown(index) {
                if (!index && index !== 0) {this.$refs.paymentDropdown.hide();}
                else {this.$refs.existingPaymentDropdown[index].hide();}
            },
            canRefund(payment) {
                if (!payment || !payment.method) {
                    return false;
                }

                const isRefundableMethod =
                    payment.method === 'PayPal' || payment.method === 'Credit Card';

                if (!isRefundableMethod) {
                    return false;
                }

                const remaining = this.getRemainingRefundable(payment);

                // Require at least a few cents left to refund
                return remaining > 0.009;
            },
            getRemainingRefundable(payment) {
                if (!payment || payment.amount == null) {
                    return 0;
                }

                const amount = parseFloat(payment.amount);
                const refunds = Array.isArray(this.refunds) ? this.refunds : [];
                const txId = payment.transactionId;

                const totalRefunded = refunds
                    .filter(r => {
                        const refundedTxId = r.paymentTransactionId;
                        const refundType = r.type;

                        // Prefer exact transaction match when we have IDs
                        if (txId && refundedTxId) {
                            return refundedTxId === txId;
                        }

                        // Fallback: group by method/type when no transactionId stored
                        if (!refundedTxId && refundType && payment.method) {
                            return refundType.toLowerCase() === payment.method.toLowerCase();
                        }

                        return false;
                    })
                    .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

                const remaining = amount - totalRefunded;

                return remaining > 0 ? remaining : 0;
            },
            openRefundModal(payment) {
                if (!payment || payment.amount == null) {
                    return;
                }

                const remaining = this.getRemainingRefundable(payment);

                this.refundPayment = payment;
                this.totalRefundedForPayment = parseFloat(payment.amount) - remaining;
                this.maxRefundAmount = remaining;
                this.refundAmount = this.maxRefundAmount > 0 ? Number(this.maxRefundAmount.toFixed(2)) : null;
                this.refundNote = '';
                this.refundError = null;
                this.showRefundModal = true;
            },
            closeRefundModal() {
                this.showRefundModal = false;
                this.refundPayment = null;
                this.refundAmount = null;
                this.refundNote = '';
                this.maxRefundAmount = 0;
                this.totalRefundedForPayment = 0;
                this.isSubmittingRefund = false;
                this.refundError = null;
            },
            async confirmRefund() {
                const orderId = this.effectiveOrderId;
                // eslint-disable-next-line no-console
                console.log('confirmRefund clicked', { orderId, refundPayment: this.refundPayment, refundAmount: this.refundAmount });
                if (!this.refundPayment) {
                    this.refundError = 'No payment selected to refund.';
                    return;
                }
                if (!orderId) {
                    this.refundError = 'Cannot process refund: missing order ID.';
                    return;
                }

                const amount = Number(this.refundAmount);

                if (!amount || amount <= 0) {
                    this.refundError = 'Refund amount must be greater than 0.';
                    return;
                }

                if (this.maxRefundAmount > 0 && amount - this.maxRefundAmount > 0.009) {
                    this.refundError = `Refund amount cannot exceed $${this.maxRefundAmount.toFixed(2)}.`;
                    return;
                }

                this.isSubmittingRefund = true;
                this.refundError = null;

                try {
                    const isPayPal = this.refundPayment.method === 'PayPal';
                    const payload = {
                        amount,
                        note: this.refundNote || undefined,
                        paymentTransactionId: this.refundPayment.transactionId,
                        reason: 'Order refund',
                        type: isPayPal ? 'paypal' : 'stripe',
                        orderId: this.effectiveOrderId,
                    };

                    if (!isPayPal) {
                        payload.chargeId = this.refundPayment.transactionId;
                    }

                    await this.refundOrder(payload);

                    // Re-fetch the full order so we get the latest refunds from the DB
                    const baseUrl = process.env.VUE_APP_ORDERS_API_URL;
                    const { data: freshOrder } = await apiClient.get(`${baseUrl}/${this.effectiveOrderId}`);

                    if (freshOrder && Array.isArray(freshOrder.refunds)) {
                        this.$emit('refunds-updated', freshOrder.refunds);
                    }

                    this.closeRefundModal();
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('Error processing refund', err);
                    this.refundError = 'There was a problem processing the refund. Please try again.';
                } finally {
                    this.isSubmittingRefund = false;
                }
            },
            async refundOrder(payload) {
                const baseUrl = process.env.VUE_APP_ORDERS_API_URL;
                // Backend route: routes/payment.js -> POST /payment/refund
                const { data } = await apiClient.post(`${baseUrl}/payment/refund`, payload);
                return data;
            }
        },
        computed: {
            canSubmitRefund() {
                return !!this.refundPayment && !!this.refundAmount && this.refundAmount > 0 && this.maxRefundAmount >= 0;
            },
            effectiveOrderId() {
                const fromProp = this.orderId;
                const fromOrder =
                    (this.order && (this.order.orderId || this.order.id)) || null;
                const fromRoute =
                    (this.$route && this.$route.params && (this.$route.params.orderId || this.$route.params.id)) ||
                    null;
                return fromProp || fromOrder || fromRoute || null;
            }
        }
    }
</script>

<style scoped>
    .editButton {
        border: none;
    }
    .refundButton {
        border-radius: 0;
    }
    .input-group-text {
        width: 8rem;
    }

    .list-group-item {
        padding: 0.25rem .5rem;
    }

    .paymentLink {
        display: flex;
        align-items: center;
    }
</style>